/**
 * HOME-SIMPLIFICATION P1 — first-engagement gate.
 *
 * Returns whether the visitor has completed a first engagement
 * (guest micro-prediction OR `vs_first_engagement_done` flag).
 * Used to defer cold-start home surfaces (Day2, NextMission,
 * NationMomentum, NationPosition) until the user has produced
 * actual data, eliminating empty "0 / —" panels.
 *
 * Purely client-side: no Auth / RLS / Realtime touched.
 */
import { useEffect, useState } from "react";
import {
  hasFirstEngagement,
  readPendingPrediction,
} from "@/lib/predictions/guestPrediction";
import { trackEvent } from "@/lib/audit";

export function useHasFirstEngagement(): boolean {
  const [engaged, setEngaged] = useState<boolean>(() =>
    hasFirstEngagement() || readPendingPrediction() !== null,
  );
  useEffect(() => {
    const recompute = () => {
      const next = hasFirstEngagement() || readPendingPrediction() !== null;
      // Defer to a microtask so we never call setState during another
      // component's render (e.g. when triggered via `vs:event` dispatched
      // from a useMemo in HomeFeedDrawer / EventTimeline).
      queueMicrotask(() => {
        setEngaged((prev) => (prev === next ? prev : next));
      });
    };
    window.addEventListener("storage", recompute);
    window.addEventListener("vs:event", recompute as EventListener);
    return () => {
      window.removeEventListener("storage", recompute);
      window.removeEventListener("vs:event", recompute as EventListener);
    };
  }, []);
  return engaged;
}

const reported = new Set<string>();

/** Emit a deferred-surface event once per session per surface. */
export function reportSurfaceDeferred(surface: string): null {
  const key = `deferred:${surface}`;
  if (!reported.has(key)) {
    reported.add(key);
    void trackEvent("home_surface_deferred", { surface });
  }
  return null;
}

/** Emit a hidden (empty-state) surface event once per session. */
export function reportSurfaceHidden(surface: string, reason: string): null {
  const key = `hidden:${surface}:${reason}`;
  if (!reported.has(key)) {
    reported.add(key);
    void trackEvent("home_surface_hidden", { surface, reason });
  }
  return null;
}

/** Emit a below-the-fold relocation event once per session. */
export function reportSurfaceBelowFold(surface: string): void {
  const key = `below:${surface}`;
  if (!reported.has(key)) {
    reported.add(key);
    void trackEvent("home_surface_below_fold", { surface });
  }
}